import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Microphone, Stop, Spinner } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";

/**
 * Empty-state onboarding components for the Agenda — shown when the user
 * has no todos yet. Includes:
 *   - OnboardingHero: 3-step explainer with "try the example" CTA
 *   - VoiceDemoPanel: scripted voice/text demo using a pre-set phrase
 *
 * Extracted from Agenda.jsx (iter 100c refactor).
 */

// ── Empty-state onboarding ──────────────────────────────────────
const EXAMPLE_PHRASE = "Oggi ho incontrato la Signora Bianchi, cerca un trilocale a Milano zona Brera sotto i 500.000 euro, deve avere ascensore e balcone. Le devo mandare entro venerdì 3 proposte che matchano.";

export function VoiceDemoPanel({ onDone, onCancel }) {
  // Staged "fake recording" UX:
  //  phase=recording → pulse + waveform + typewriter
  //  phase=sending   → spinner "Trascrivo + organizzo…"
  const [phase, setPhase] = useState("recording");
  const [typed, setTyped] = useState("");
  const fullText = EXAMPLE_PHRASE;

  // Typewriter: ~18ms per char ≈ 3.6s for 200 chars
  useEffect(() => {
    if (phase !== "recording") return;
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setTyped(fullText.slice(0, i));
      if (i >= fullText.length) clearInterval(id);
    }, 18);
    return () => clearInterval(id);
  }, [phase, fullText]);

  const finish = async () => {
    setPhase("sending");
    try {
      await api.post("/agenda/text", { text: fullText });
      onDone();
    } catch (e) {
      toast.error(apiError(e, "Errore esempio"));
      onCancel();
    }
  };

  // 22 bars with staggered animation
  const bars = Array.from({ length: 22 });

  return (
    <div className="px-6 py-5 bg-gradient-to-br from-[#1C1C1A] to-[#2A2A28] text-white" data-testid="voice-demo-panel">
      {phase === "recording" ? (
        <>
          <div className="flex items-center gap-3 mb-3">
            <div className="relative shrink-0">
              <div className="absolute inset-0 rounded-full bg-[#E63946] animate-ping opacity-40"></div>
              <div className="relative w-11 h-11 rounded-full bg-[#E63946] text-white flex items-center justify-center shadow-lg">
                <Microphone size={20} weight="fill" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#C8A66B] font-semibold">In ascolto…</div>
              <div className="text-[13px] text-white/80">Pronto trascrive mentre parli</div>
            </div>
            <button
              onClick={onCancel}
              className="text-[11px] text-white/60 hover:text-white underline underline-offset-2"
              data-testid="voice-demo-cancel"
            >
              annulla
            </button>
          </div>

          {/* Waveform */}
          <div className="flex items-end justify-center gap-[3px] h-10 mb-4" aria-hidden="true">
            {bars.map((_, i) => (
              <span
                key={i}
                className="w-[3px] bg-[#7C8B6E] rounded-full voice-demo-bar"
                style={{
                  animationDelay: `${(i * 0.08) % 1.1}s`,
                  height: `${20 + ((i * 17) % 70)}%`,
                }}
              />
            ))}
          </div>

          {/* Typed phrase */}
          <div className="bg-white/[0.06] border border-white/[0.08] rounded-lg px-4 py-3 min-h-[84px] text-[13.5px] leading-[1.55] font-mono text-white/90">
            {typed}
            <span className="inline-block w-[7px] h-[14px] bg-[#7C8B6E] ml-0.5 align-middle animate-pulse" />
          </div>

          {/* Finish CTA only when fully typed */}
          {typed.length >= fullText.length && (
            <button
              onClick={finish}
              className="mt-4 w-full py-2.5 bg-[#7C8B6E] hover:bg-[#6B7A5D] text-white text-[13px] font-medium rounded-md inline-flex items-center justify-center gap-2 transition-colors"
              data-testid="voice-demo-finish"
            >
              <Stop size={14} weight="fill" /> Fine · vedi cosa ne esce
            </button>
          )}
        </>
      ) : (
        <div className="py-4 flex items-center gap-3">
          <Spinner size={22} weight="bold" className="animate-spin text-[#C8A66B]" />
          <div>
            <div className="text-[13.5px] font-semibold">Trascrivo + organizzo…</div>
            <div className="text-[12px] text-white/70 mt-0.5">Contatto, budget, zone, promemoria</div>
          </div>
        </div>
      )}
    </div>
  );
}

export function OnboardingHero({ onTryExample }) {
  const [demo, setDemo] = useState(false);

  return (
    <div className="bg-white border border-[#E8E5DE] rounded-2xl overflow-hidden" data-testid="agenda-onboarding">
      <div className="bg-gradient-to-br from-[#7C8B6E] to-[#5A6B4D] text-white px-6 py-7">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-90 mb-1">Come funziona</div>
        <h2 className="text-[22px] lg:text-[26px] font-serif leading-[1.15]">
          Tra una visita e l'altra, <span className="italic">parla con Pronto</span>.
        </h2>
        <p className="text-[13px] opacity-90 mt-1 leading-[1.5] max-w-lg">
          Al volo, in macchina, in pausa caffè. Pronto ascolta e organizza tutto per te.
        </p>
      </div>
      <div className="divide-y divide-[#EDEAE2]">
        {[
          {
            n: "1",
            title: "Premi il microfono",
            desc: "Il bottone bianco grande in alto. Puoi parlare o scrivere.",
          },
          {
            n: "2",
            title: "Racconta la tua giornata",
            desc: "\"Oggi ho visto la signora Ferrari, budget 350k, zona Isola, vuole il box, le devo mandare 3 proposte entro sabato\"",
          },
          {
            n: "3",
            title: "Pronto fa il resto",
            desc: "Crea il contatto Ferrari con budget e zone, aggiunge il promemoria \"Mandare 3 proposte\" per sabato, lo mette in prima pagina domattina.",
          },
        ].map(({ n, title, desc }) => (
          <div key={n} className="flex gap-4 px-6 py-4">
            <div className="w-8 h-8 rounded-full bg-[#EDEAE2] text-[#7C8B6E] flex items-center justify-center font-serif text-[14px] shrink-0 font-semibold">{n}</div>
            <div className="flex-1">
              <div className="text-[13.5px] font-semibold text-[#1C1C1A]">{title}</div>
              <div className="text-[12.5px] text-[#6B6A65] mt-0.5 leading-[1.5]">{desc}</div>
            </div>
          </div>
        ))}
      </div>
      {demo ? (
        <VoiceDemoPanel onDone={onTryExample} onCancel={() => setDemo(false)} />
      ) : (
        <div className="px-6 py-5 bg-[#FAFAF7] border-t border-[#EDEAE2] flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setDemo(true)}
            className="inline-flex items-center gap-2 bg-[#1C1C1A] hover:bg-black text-white text-[13px] font-medium px-4 py-2.5 rounded-md transition-colors"
            data-testid="onboarding-try-example"
          >
            <Microphone size={13} weight="fill" /> Prova col microfono
          </button>
          <span className="text-[11.5px] text-[#9A968B] w-full lg:w-auto lg:ml-auto">
            Parla con Pronto in qualsiasi momento — gli altri contatti arrivano da soli.
          </span>
        </div>
      )}
    </div>
  );
}

